/**
 * Anthropic provider options schema.
 *
 * Defines typed providerOptions for Anthropic (Claude) models, providing
 * TypeScript autocompletion and runtime validation.
 *
 * @see https://platform.claude.com/docs/en/api/messages
 * @module
 */

import { z } from 'zod';

/**
 * Anthropic provider options schema.
 *
 * These options are passed directly to the Anthropic Messages API and control
 * various aspects of request handling and generation.
 *
 * @example
 * ```typescript
 * providerOptions: {
 *   service_tier: 'auto',
 *   metadata: { user_id: 'user-123' },
 *   cacheSystemPrompt: true,
 * }
 * ```
 */
export const anthropicProviderOptions = z.object({
  /** Request priority tier: 'auto' uses priority capacity when available, 'standard_only' opts out */
  service_tier: z.enum(['auto', 'standard_only']).optional(),

  /** Request metadata. user_id is an opaque identifier for abuse monitoring */
  metadata: z.object({
    user_id: z.string().optional(),
  }).optional(),

  /**
   * Cache the system prompt with a prompt-caching breakpoint.
   * Adds `cache_control: { type: 'ephemeral' }` to the system prompt so
   * repeated requests with the same prefix bill cached-input rates.
   */
  cacheSystemPrompt: z.boolean().optional(),

  /**
   * Explicit thinking configuration override. When set, this takes precedence
   * over the thinking config derived from the request's reasoning level.
   * Accepts the Anthropic API shape, e.g. `{ type: 'adaptive' }` or
   * `{ type: 'enabled', budget_tokens: 8192 }`.
   */
  thinking: z.record(z.string(), z.unknown()).optional(),
}).passthrough(); // Allow unknown keys for forward compatibility

/**
 * TypeScript type for Anthropic provider options.
 * Inferred from the Zod schema for type-safe usage.
 */
export type AnthropicProviderOptions = z.infer<typeof anthropicProviderOptions>;
